import React, { useRef, useState } from 'react';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { Button, Card, Col, Flex, Row, Tooltip, Tour, TourProps } from 'antd';

import IssuesAndInsights from '@totem/components/services/remoteAccess/v2/summary/issuesAndInsights';
import OrganizationalUsage from '@totem/components/services/remoteAccess/v2/summary/organizationalUsage';
import PropertySelector from '@totem/components/services/remoteAccess/v2/summary/propertySelector';
import PropertySummary from '@totem/components/services/remoteAccess/v2/summary/propertySummary';
import RemoteAccessDashboardContainer from '@totem/components/services/remoteAccess/v2/summary/remoteAccessDashboardContainer';
import RiskAssessment from '@totem/components/services/remoteAccess/v2/summary/riskAssessment';
import UserSessionActivity from '@totem/components/services/remoteAccess/v2/summary/userSessionActivity';
import UserSummary from '@totem/components/services/remoteAccess/v2/summary/userSummary';
import { statisticsStyles } from '@totem/components/ui_components/widgets/statistic/IBStatistic';

import './styles.css';

const styles = {
  card: {
    backgroundColor: '#DAE8F8',
    padding: '16px',
    height: '100%',
  },
  rows: {
    padding: '4px',
  },
  chartRow: {
    paddingLeft: '4px',
    paddingRight: '4px',
    paddingTop: '16px',
    paddingBottom: '4px',
  },
};

const RemoteAccessSummaryV2 = () => {
  const [tourOpen, setTourOpen] = useState(false);

  const propertySelectorRef = useRef<HTMLDivElement>(null);
  const userSummaryRef = useRef<HTMLDivElement>(null);
  const issuesRef = useRef<HTMLDivElement>(null);
  const riskRef = useRef<HTMLDivElement>(null);
  const sessionRef = useRef<HTMLDivElement>(null);
  const orgUsageRef = useRef<HTMLDivElement>(null);
  const propertySummaryRef = useRef<HTMLDivElement>(null);

  const tourSteps: TourProps['steps'] = [
    {
      title: 'Remote Access Governance',
      description:
        'This dashboard gives you a full picture of remote access activity, risk, and user behaviour across your organization.',
      placement: 'center',
      target: null,
    },
    {
      title: 'Property Filter',
      description:
        'Use this selector to filter all dashboard panels to a specific property or building. Selecting "All" shows data across your entire portfolio.',
      target: () => propertySelectorRef.current,
      placement: 'bottomRight',
    },
    {
      title: 'User Summary',
      description:
        'At-a-glance counts of total users, active sessions, and other key user metrics for the selected property scope.',
      target: () => userSummaryRef.current,
      placement: 'bottom',
    },
    {
      title: 'Issues & Insights',
      description:
        'Highlights active problems and actionable insights detected across your remote access environment — things that may need immediate attention.',
      target: () => issuesRef.current,
      placement: 'right',
    },
    {
      title: 'Risk Assessment',
      description:
        'Shows the current risk posture for remote access. "Organizations" here includes both the customer organization and their vendors — the chart helps identify which groups are exhibiting riskier behaviour based on user activity, policy compliance, and open vulnerabilities.',
      target: () => riskRef.current,
      placement: 'left',
    },
    {
      title: 'Remote Access Session Summary',
      description:
        'A chart of remote access sessions over time, helping you spot usage trends and unexpected spikes.',
      target: () => sessionRef.current,
      placement: 'right',
    },
    {
      title: 'User Organizational Usage',
      description:
        'Shows remote access usage distributed across organizations over the last 30 days. "Organizations" includes both the customer organization and their vendors — use this chart to spot which vendors or groups are accessing systems most frequently or exhibiting patterns that warrant closer review.',
      target: () => orgUsageRef.current,
      placement: 'left',
    },
    {
      title: 'Property Summary',
      description:
        'A breakdown of remote access status per property — useful for comparing compliance and activity across sites.',
      target: () => propertySummaryRef.current,
      placement: 'top',
    },
  ];

  return (
    <RemoteAccessDashboardContainer>
      <div styleName={'sizeablePage'}>
        <div style={styles.rows}>
          <Flex justify="space-between" align="middle">
            <div>
              <span style={statisticsStyles.title}>
                Remote Access Governance
              </span>
            </div>
            <div
              ref={propertySelectorRef}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
            >
              <PropertySelector />
              <Tooltip title="Help / Tour">
                <Button
                  type="text"
                  icon={<QuestionCircleOutlined />}
                  onClick={() => setTourOpen(true)}
                />
              </Tooltip>
            </div>
          </Flex>
        </div>
        <div ref={userSummaryRef} styleName={'sizeableStats'}>
          <UserSummary />
        </div>
        <div style={styles.rows}>
          <Row gutter={64} style={{ alignItems: 'stretch' }}>
            <Col
              ref={issuesRef}
              span={12}
              style={{ display: 'flex', flexDirection: 'column' }}
            >
              <IssuesAndInsights />
            </Col>
            <Col
              ref={riskRef}
              span={12}
              style={{ display: 'flex', flexDirection: 'column' }}
            >
              <RiskAssessment />
            </Col>
          </Row>
        </div>
        <div style={styles.chartRow}>
          <Row gutter={64}>
            <Col ref={sessionRef} span={12}>
              <Card title={'Remote Access Session Summary'}>
                <UserSessionActivity />
              </Card>
            </Col>
            <Col ref={orgUsageRef} span={12}>
              <Card
                styles={{
                  header: {
                    borderBottomStyle: 'solid',
                    borderBottomColor: statisticsStyles.warning.color,
                  },
                }}
                title={
                  <span
                    style={{
                      color: statisticsStyles.warning.color,
                    }}
                  >
                    User Organizational Usage (Last 30 days)
                  </span>
                }
              >
                <OrganizationalUsage />
              </Card>
            </Col>
          </Row>
        </div>
        <div ref={propertySummaryRef} styleName={'sizeableStats'}>
          <PropertySummary />
        </div>
      </div>
      <Tour
        open={tourOpen}
        onClose={() => setTourOpen(false)}
        steps={tourSteps}
      />
    </RemoteAccessDashboardContainer>
  );
};

export default RemoteAccessSummaryV2;
